import React, { useEffect, useState } from 'react';

import Button from 'components/core/Button';
import Checkbox from 'components/core/Checkbox';
import CoverTile from 'components/core/CoverTile';
import DateTimeInput from 'components/core/DateTimeInput';
import Modal from 'components/core/Modal';
import SegmentedControl from 'components/core/SegmentedControl';
import TextInput from 'components/core/TextInput';
import MediaSource from 'components/MediaSource';

import { uploadMedia } from 'api/media';
import { useGetSegments } from 'api/audience';
import { DEFAULT_STORY_LOCALE, STORY_LOCALES, TStoryLocale } from 'constants/storyLocales';
import { missingAttributeKeys } from 'helpers/audience';
import { getImageDimensions } from 'helpers/image';
import { TLocalizedText } from 'types/story';

import AudiencePicker from './AudiencePicker';
import { StyledField, StyledFieldRow, StyledLabel, StyledNote } from './CreateGroupModal.styles';

const MIN_COVER_SIZE = 400;

const coverNoteFor = (dims: { width: number; height: number } | null): string | null => {
  if (!dims) {
    return null;
  }
  const ratio = dims.width / dims.height;
  const notSquare = ratio < 0.9 || ratio > 1.1;
  const tooSmall = Math.min(dims.width, dims.height) < MIN_COVER_SIZE;
  if (notSquare || tooSmall) {
    return `Story covers look best square, ≥ ${MIN_COVER_SIZE}×${MIN_COVER_SIZE} (500×500 recommended). This one is ${dims.width}×${dims.height}.`;
  }
  return null;
};

export type TGroupForm = {
  name: string;
  title: string;
  titleTranslations?: Partial<Record<TStoryLocale, string>>;
  /** Per-locale covers. `en` is required to publish; the rest fall back to it on the device. */
  coverMediaUrl?: TLocalizedText;
  startsAt?: string | null;
  endsAt?: string | null;
  segmentId?: string | null;
  pinned: boolean;
};

export const EMPTY_GROUP_FORM: TGroupForm = {
  name: '',
  title: '',
  coverMediaUrl: {},
  startsAt: null,
  endsAt: null,
  segmentId: null,
  pinned: false,
};

type TCreateGroupModalProps = {
  isOpen: boolean;
  mode: 'create' | 'edit';
  initialForm: TGroupForm;
  placeholders?: string[];
  onClose: () => void;
  onSubmit: (form: TGroupForm) => void;
};

const TITLE_LOCALES = STORY_LOCALES.filter((locale) => locale.code !== DEFAULT_STORY_LOCALE);

const CreateGroupModal = ({
  isOpen,
  mode,
  initialForm,
  placeholders = [],
  onClose,
  onSubmit,
}: TCreateGroupModalProps) => {
  const [form, setForm] = useState<TGroupForm>(initialForm);
  const [activeTitleLocale, setActiveTitleLocale] = useState<TStoryLocale>(TITLE_LOCALES[0].code);
  const [coverNote, setCoverNote] = useState<string | null>(null);
  const [isUploadingCover, setIsUploadingCover] = useState(false);
  const [activeCoverLocale, setActiveCoverLocale] = useState<TStoryLocale>(DEFAULT_STORY_LOCALE);
  const isCreate = mode === 'create';

  /*
   * Computed here rather than inside `AudiencePicker` because the footer has to gate on it: an
   * audience that cannot supply a placeholder the slides use would render as an empty string on
   * the device. Shares the picker's React Query cache, so this costs no extra request.
   */
  const { segments } = useGetSegments();
  const selectedSegment = form.segmentId
    ? segments.find((segment) => segment.id === form.segmentId)
    : undefined;
  const missingKeys = missingAttributeKeys(placeholders, selectedSegment?.attributeKeys ?? []);

  useEffect(() => {
    if (isOpen) {
      setForm(initialForm);
      setActiveTitleLocale(TITLE_LOCALES[0].code);
    }
  }, [isOpen, initialForm]);

  const coverForLocale = form.coverMediaUrl?.[activeCoverLocale];
  const setCoverForLocale = (url: string | undefined) =>
    setForm((prev) => {
      const next = { ...(prev.coverMediaUrl ?? {}) };
      if (url) {
        next[activeCoverLocale] = url;
      } else {
        delete next[activeCoverLocale];
      }
      return { ...prev, coverMediaUrl: next };
    });

  useEffect(() => {
    if (!coverForLocale) {
      setCoverNote(null);
      return undefined;
    }
    let active = true;
    getImageDimensions(coverForLocale).then((dims) => {
      if (active) {
        setCoverNote(coverNoteFor(dims));
      }
    });
    return () => {
      active = false;
    };
  }, [coverForLocale]);

  const uploadCover = async (file: Blob, filename: string) => {
    setIsUploadingCover(true);
    try {
      const item = await uploadMedia(file, 'IMAGE', filename);
      setCoverForLocale(item.mediaUrl);
    } catch (error) {
      setCoverNote(error instanceof Error ? error.message : 'Cover upload failed.');
    } finally {
      setIsUploadingCover(false);
    }
  };

  const handleDropCover = (file: File) => uploadCover(file, file.name);

  const handlePickCover = async (url: string, mediaId?: string) => {
    if (mediaId) {
      setCoverForLocale(url);
      return;
    }
    const blob = await fetch(url).then((response) => response.blob());
    await uploadCover(blob, 'cover.jpg');
  };

  const coverLocaleOptions = STORY_LOCALES.map((locale) => ({
    value: locale.code,
    label: `${locale.label}${locale.code === DEFAULT_STORY_LOCALE ? ' *' : ''}${
      form.coverMediaUrl?.[locale.code] ? ' ✓' : ''
    }`,
  }));

  const titleLocaleOptions = TITLE_LOCALES.map((locale) => ({
    value: locale.code,
    label: `${locale.label}${(form.titleTranslations?.[locale.code] ?? '').trim() ? ' ✓' : ''}`,
  }));

  const setTitleTranslation = (locale: TStoryLocale, value: string) =>
    setForm((prev) => ({
      ...prev,
      titleTranslations: { ...prev.titleTranslations, [locale]: value },
    }));

  const submit = () => {
    const titleTranslations = Object.fromEntries(
      Object.entries(form.titleTranslations ?? {}).filter(([, value]) => value?.trim())
    ) as TGroupForm['titleTranslations'];
    onSubmit({
      ...form,
      name: form.name.trim() || 'Untitled group',
      title: form.title.trim(),
      titleTranslations: Object.keys(titleTranslations ?? {}).length
        ? titleTranslations
        : undefined,
    });
  };

  const canSubmit = (isCreate || !!form.title.trim()) && missingKeys.length === 0;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isCreate ? 'Create story group' : 'Group details'}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" disabled={!canSubmit || isUploadingCover} onClick={submit}>
            {isCreate ? 'Create' : 'Save'}
          </Button>
        </>
      }>
      <StyledField>
        <StyledLabel>Name (internal)</StyledLabel>
        <TextInput
          label="Name (internal)"
          value={form.name}
          placeholder="e.g. Summer sale"
          onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))}
        />
        {isCreate ? (
          <StyledNote>You’ll add slides, cover, schedule and audience in the editor.</StyledNote>
        ) : null}
      </StyledField>

      {isCreate ? null : (
        <>
          <StyledField>
            <StyledLabel>Title (shown to merchants)</StyledLabel>
            <TextInput
              label="Title (shown to merchants)"
              value={form.title}
              placeholder="e.g. 10% AI cashback"
              onChange={(event) => setForm((prev) => ({ ...prev, title: event.target.value }))}
            />
            <StyledNote $tone="warning">
              The English title is required — it’s what merchants see, and the group can’t go live
              without it.
            </StyledNote>
          </StyledField>

          <StyledField>
            <StyledLabel>Title translations (optional)</StyledLabel>
            <SegmentedControl
              label="Title translations (optional)"
              value={activeTitleLocale}
              options={titleLocaleOptions}
              onChange={(value) => setActiveTitleLocale(value as TStoryLocale)}
            />
            <TextInput
              label="Title translation"
              value={form.titleTranslations?.[activeTitleLocale] ?? ''}
              placeholder={form.title || 'English title'}
              onChange={(event) => setTitleTranslation(activeTitleLocale, event.target.value)}
            />
          </StyledField>

          <StyledField>
            <StyledLabel>Cover</StyledLabel>
            <SegmentedControl
              label="Cover language"
              value={activeCoverLocale}
              options={coverLocaleOptions}
              onChange={(value) => setActiveCoverLocale(value as TStoryLocale)}
            />
            <CoverTile
              aria-label="Cover"
              as="div"
              image={coverForLocale ?? form.coverMediaUrl?.[DEFAULT_STORY_LOCALE] ?? undefined}
              height={96}
              accept="image/*"
              onDropFile={handleDropCover}>
              {coverForLocale ? '' : 'Drop an image here, or use the buttons below'}
            </CoverTile>
            {activeCoverLocale !== DEFAULT_STORY_LOCALE && !coverForLocale ? (
              <StyledNote>Falling back to the English cover for this language.</StyledNote>
            ) : null}
            <MediaSource
              mediaTypes={['IMAGE']}
              disabled={isUploadingCover}
              onPick={(url, _mediaType, mediaId) => handlePickCover(url, mediaId)}
            />
            {isUploadingCover ? <StyledNote>Uploading cover…</StyledNote> : null}
            {coverNote ? <StyledNote $tone="warning">{coverNote}</StyledNote> : null}
          </StyledField>

          <StyledFieldRow>
            <StyledField>
              <StyledLabel>Starts at</StyledLabel>
              <DateTimeInput
                label="Starts at"
                value={form.startsAt ?? undefined}
                placeholder="No start"
                onChange={(iso) => setForm((prev) => ({ ...prev, startsAt: iso ?? null }))}
              />
            </StyledField>
            <StyledField>
              <StyledLabel>Ends at</StyledLabel>
              <DateTimeInput
                label="Ends at"
                value={form.endsAt ?? undefined}
                placeholder="No end"
                onChange={(iso) => setForm((prev) => ({ ...prev, endsAt: iso ?? null }))}
              />
            </StyledField>
          </StyledFieldRow>

          <StyledField>
            <StyledLabel>Audience</StyledLabel>
            <AudiencePicker
              value={form.segmentId}
              missingKeys={missingKeys}
              onChange={(segmentId) =>
                setForm((prev) => ({ ...prev, segmentId: segmentId ?? null }))
              }
            />
          </StyledField>

          <StyledField>
            <Checkbox
              checked={form.pinned}
              label="Pinned to the top of the tray"
              onChange={(checked) => setForm((prev) => ({ ...prev, pinned: checked }))}
            />
          </StyledField>
        </>
      )}
    </Modal>
  );
};

export default CreateGroupModal;
